import React, {
	memo,
	useCallback,
	useEffect,
	useRef,
	useState,
	Fragment,
	type PropsWithChildren,
} from 'react';
import { styled } from '@compiled/react';
import defer from 'lodash/defer';
import Button, { LoadingButton } from '@atlaskit/button';
import Form, { ErrorMessage, Field } from '@atlaskit/form';
import Heading from '@atlaskit/heading';
import ChevronRightIconLegacy from '@atlaskit/icon/glyph/chevron-right';
import ChevronRightIcon from '@atlaskit/icon/utility/chevron-right';
import { Box, Flex, Inline, xcss } from '@atlaskit/primitives';
import TextField from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { useIntl } from '@atlassian/jira-intl';
import {
	fireTrackAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { getRandomColor } from '../../utils/colors/index.tsx';
import { CategoryColorPicker } from './category-color-picker/index.tsx';
import CategoryTag from './category-tag/index.tsx';
import messages from './messages.tsx';
import type { CategoryData } from './types.tsx';

type Props = {
	onCancel: () => void;
	onSubmit: (data: CategoryData) => void;
	initialData: CategoryData;
	type?: 'create' | 'edit';
};

const CategoryForm = ({ onCancel, onSubmit, initialData, type }: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { categoryColor, categoryName } = initialData;
	const { formatMessage } = useIntl();
	const [currentColor, setCurrentColor] = useState<string>(
		categoryColor === null ? getRandomColor() : categoryColor,
	);
	const [currentName, setCurrentName] = useState<string>(categoryName);
	const inputElement = useRef<HTMLInputElement>(null);

	useEffect(() => {
		fireTrackAnalytics(createAnalyticsEvent({}), 'CategoryForm viewed', { purpose: type });
	}, [createAnalyticsEvent, type]);

	useEffect(() => {
		if (inputElement.current) {
			// This ensures that the input element is focused after the component is rendered
			defer(() => inputElement?.current?.focus());
		}
	}, []);

	const isValidCategoryName = useCallback(
		(value?: string) => {
			// Blank category name validation
			if (!value || value.trim() === '') {
				return formatMessage(messages.errorMessageBlankCategoryName);
			}
			// TODO: GROW-389 Duplicate category name validation
			return undefined;
		},
		[formatMessage],
	);

	const handleSubmit = useCallback(
		(formData: CategoryData) => {
			fireUIAnalytics(
				createAnalyticsEvent({}),
				'button clicked',
				type === 'edit' ? 'categoryFormSave' : 'categoryFormAdd',
			);
			onSubmit && onSubmit(formData);
		},
		[createAnalyticsEvent, onSubmit, type],
	);

	return (
		<Box xcss={formStyles} backgroundColor="elevation.surface.overlay">
			<Inline xcss={previewStyles} testId="business-categories.ui.category-form.preview">
				<Heading as="h4" size="small">
					{type === 'edit'
						? formatMessage(messages.editCategoryTitle)
						: formatMessage(messages.newCategoryTitle)}
				</Heading>
				<ChevronRightIcon
					label=""
					color={token('color.icon.subtle')}
					spacing={isVisualRefreshEnabled() ? 'spacious' : 'none'}
					LEGACY_fallbackIcon={ChevronRightIconLegacy}
				/>
				{currentName !== '' && (
					<TagContainer>
						<CategoryTag categoryName={currentName} categoryColor={currentColor} />
					</TagContainer>
				)}
			</Inline>
			<Form onSubmit={handleSubmit}>
				{({ formProps, submitting }) => (
					<StyledForm
						{...formProps}
						autoComplete="off"
						data-testid="business-categories.ui.category-form.form"
					>
						<FieldsWrapper>
							<Field
								name="categoryName"
								defaultValue={initialData.categoryName}
								testId="business-categories.ui.category-form.category-name"
								validate={isValidCategoryName}
							>
								{({ fieldProps, error }) => (
									<>
										<TextField
											{...fieldProps}
											aria-label={formatMessage(messages.categoryNameLabel)}
											autoComplete="off"
											autoFocus
											ref={inputElement}
											placeholder={formatMessage(messages.categoryNamePlaceholder)}
											onChange={(e) => {
												setCurrentName(e.currentTarget.value);
												fieldProps.onChange(e);
											}}
										/>
										{error && <ErrorMessage>{error}</ErrorMessage>}
									</>
								)}
							</Field>
						</FieldsWrapper>
						<FieldsWrapper>
							<Field name="categoryColor" defaultValue={currentColor}>
								{({ fieldProps }) => (
									<CategoryColorPicker
										{...fieldProps}
										onChangeColor={(newColor: string) => {
											setCurrentColor(newColor);
											fieldProps.onChange(newColor);
											fireUIAnalytics(
												createAnalyticsEvent({}),
												'color clicked',
												'categoryFormColorPicker',
												{ color: newColor },
											);
										}}
										color={currentColor}
									/>
								)}
							</Field>
						</FieldsWrapper>
						<Flex gap="space.100" justifyContent="end">
							<Button
								appearance="subtle"
								testId="business-categories.ui.category-form.cancel-button"
								onClick={() => {
									onCancel();
									fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'categoryFormCancel');
								}}
							>
								{formatMessage(messages.cancelButton)}
							</Button>
							<LoadingButton
								appearance="primary"
								type="submit"
								isLoading={submitting}
								testId="business-categories.ui.category-form.submit-button"
							>
								{type === 'edit'
									? formatMessage(messages.saveButton)
									: formatMessage(messages.addButton)}
							</LoadingButton>
						</Flex>
					</StyledForm>
				)}
			</Form>
		</Box>
	);
};

export default memo<Props>(CategoryForm);

const formStyles = xcss({
	marginBlockStart: 'space.100',
	borderRadius: 'border.radius.200',
	boxShadow: 'elevation.shadow.overlay',
	display: 'block',
	padding: 'space.200',
	width: 'fit-content',
});

const previewStyles = xcss({
	whiteSpace: 'nowrap',
	maxWidth: '360px',
});

const tagContainerStyles = xcss({
	display: 'grid',
	paddingTop: 'space.025',
});

const TagContainer = componentWithCondition(
	isVisualRefreshEnabled,
	({ children }: PropsWithChildren<{}>) => <Box xcss={tagContainerStyles}>{children}</Box>,
	Fragment,
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledForm = styled.form({
	width: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FieldsWrapper = styled.div({
	marginBottom: token('space.200'),
});
